import { IconArrowLeft, IconAt } from "@tabler/icons-react";
import {
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import "./styles.scss";
import React, { useState } from "react";
import { AppName, Colors, LogoFullWhite } from "../../Lib/Data";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  validateEmail,
} from "../../Lib/Methods";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/Endpoints";
import { motion } from "framer-motion";

const ForgotPassword: React.FC = () => {
  document.title = `Forgot Password | ${AppName}`;

  const navigate = useNavigate();
  const emailForm = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) =>
        validateEmail(value) ? null : "Please enter a valid email!",
    },
  });
  const [isLoading, setLoading] = useState<boolean>(false);
  const SendResetEmail = async () => {
    const { hasErrors } = emailForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const { email } = emailForm.values;
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetSendPasswordResetLink(),
        data: {
          email,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r.status === 200) {
        navigate("/login");
        DefaultSuccessNotification(
          "Password reset link sent!",
          "Check your email"
        );
      }
      if (r.status === 404) {
        DefaultErrorNotification("Account does not exist!");
      }
    }
  };

  return (
    <div className="forgot-password-wrapper">
      {/* ─── Brand Panel (Left) ─── */}
      <motion.div
        className="forgot-brand-panel"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="brand-content">
          <img src={LogoFullWhite} alt={AppName} className="brand-logo" />
          <motion.h2
            className="brand-headline"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Don't worry,
            <br />
            we've got you.
          </motion.h2>
          <motion.p
            className="brand-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            Enter your email and we'll send you a link to reset your password securely.
          </motion.p>
        </div>
      </motion.div>

      {/* ─── Form Panel (Right) ─── */}
      <div className="forgot-form-panel">
        <motion.div
          className="forgot-form-inner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Stack gap={2} mb={32}>
            <Title order={2} fw={800} size={26}>
              Reset Password
            </Title>
            <Text c="dimmed" size="sm" fw={400}>
              Enter your email to receive a reset link
            </Text>
          </Stack>

          <Stack gap="sm">
            <TextInput
              label="Email Address"
              {...emailForm.getInputProps("email")}
              leftSection={<IconAt size={15} stroke={1.8} />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  SendResetEmail();
                }
              }}
              placeholder="you@email.com"
              required
              size="sm"
              classNames={{ input: "auth-input" }}
            />

            <Button
              fullWidth
              mt="sm"
              onClick={SendResetEmail}
              loading={isLoading}
              className="reset-btn"
              color={Colors.GreenPrimary}
            >
              Send Reset Link
            </Button>
          </Stack>

          <Group mt="xl" justify="center">
            <Anchor component={Link} to="/login" size="sm" c="dimmed" fw={600}>
              <Center inline>
                <IconArrowLeft size={14} stroke={2} />
                <Box ml={5}>Back to login</Box>
              </Center>
            </Anchor>
          </Group>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
